import { useState, useEffect } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { useReportDeterministic } from "./useReportDeterministic";
import { ParsedSummary, getProp } from "./ParsedSummary";
import { getStudentList } from "./ParsedSummary/studentExecutiveSummary";
import { questionNumberFrom } from "./ParsedSummary/questionBreakdown";
import { SummaryDeprecated } from "./SummaryDeprecated";
import { QuestionSummary } from "./QuestionSummary";

// Derives the per-question { proficiency_aligned, collaboration_level } items
// that QuestionSummary needs directly from the session analysis summary data,
// removing the dependency on session_history_challenges.
export const deriveQuestionItems = (summaryData) => {
  const breakdownData = getProp(summaryData, ['question', 'breakdown', 'showcase']);
  const questionList = Array.isArray(breakdownData)
    ? breakdownData
    : getProp(breakdownData, 'question');
  if (!Array.isArray(questionList) || questionList.length === 0) return [];

  return [...questionList]
    .sort((a, b) => {
      // The same rule the question detail sorts by: only a real number orders a
      // question, and one without a usable number sorts last. `Number("Q2")` is
      // NaN, and a comparator returning NaN sorts nothing at all — which left
      // this grid in whatever order the model happened to emit.
      const aNum = questionNumberFrom(getProp(a, ['questionNumber', 'number', 'question_number']));
      const bNum = questionNumberFrom(getProp(b, ['questionNumber', 'number', 'question_number']));
      return (aNum ?? Number.MAX_SAFE_INTEGER) - (bNum ?? Number.MAX_SAFE_INTEGER);
    })
    .map((question) => {
      const consensus = getProp(question, ['reachedCorrectConsensus', 'consensus', 'reached']);
      const collaboration = getProp(question, 'collaboration');
      const level = getProp(collaboration, ['level', 'category']);

      // v9 stopped writing both of these: the backend owns them now. Deriving
      // `false` from a key the prompt never emits states, of a group that may
      // have been entirely correct, that it failed — so a question the payload
      // says nothing about is dropped rather than answered for.
      if (consensus === undefined && level === undefined) return null;

      const consensusStr = consensus ? String(consensus).trim().toLowerCase() : '';
      const proficiency_aligned = consensusStr.startsWith('yes') || consensusStr === 'true';

      const levelMatch = level ? String(level).match(/\d+/) : null;
      const collaboration_level = levelMatch ? Number(levelMatch[0]) : null;

      return { proficiency_aligned, collaboration_level };
    })
    .filter(Boolean);
};

// Which per-question items the Group Results grid shows.
//
// The Proficiency and Collaboration rows point at the backend: the
// same numbers the students saw during the session, carried on the challenge
// rows as `proficiency_aligned` (the deterministic consensus) and
// `collaboration_level`. The LLM's retelling of both is the pre-cutoff path.
//
// The challenge rows arrive on their own request, so until they land we keep
// showing the LLM's version rather than blanking the grid — QuestionSummary
// renders nothing for an empty list, and a section that vanishes and comes back
// reads worse than one that settles.
export const selectQuestionItems = ({ deterministic, challengeData, summaryData }) => {
  if (deterministic && challengeData?.length) return challengeData;
  return deriveQuestionItems(summaryData);
};

export const Summary = ({
  summaryData,
  questionsOnly,
  studentsOnly,
  initialStudentName,
  initialQuestionIndex,
  isShortSession,
  value,
  index,
  challengeDataErrors,
  setChallengeDataErrors,
  challengeData,
  isChallengeDataLoading,
  fetchChallengeData,
  onSelectQuestion,
  onViewIncidents,
  forceDeterministic = false,
  educatorId = null,
  reconstructCollabLevels = false,
  showQuestionCard = true,
  reportLocation,
}) => {
  // Every figure the report shows from the backend, resolved by the one builder
  // every surface shares.
  const deterministicData = useReportDeterministic({
    value,
    challengeData,
    // The report names its own students; this is who they are.
    participantIds: summaryData?._participant_ids,
    forceDeterministic,
    educatorId,
    reconstructCollabLevels,
  });
  const deterministic = deterministicData.enabled;
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(initialQuestionIndex || 0);

  // Keep the carousel in sync when a question is picked from the Question
  // Summary on the Session Summary tab (which switches to this tab).
  useEffect(() => {
    if (questionsOnly && initialQuestionIndex != null) {
      setCurrentQuestionIndex(initialQuestionIndex);
    }
  }, [questionsOnly, initialQuestionIndex]);
  const [currentStudentIndex, setCurrentStudentIndex] = useState(() => {
    if (initialStudentName && studentsOnly) {
      const list = getStudentList(summaryData);
      const idx = list.findIndex(s => {
        const name = getProp(s, ['name', 'student']);
        return name && name.toLowerCase() === initialStudentName.toLowerCase();
      });
      return idx >= 0 ? idx : 0;
    }
    return 0;
  });

  // Get the Question-by-Question Breakdown title from summary data
  const getBreakdownTitle = () => {
    const questionBreakdownData = getProp(summaryData, ['question', 'breakdown', 'showcase']);
    const title = getProp(questionBreakdownData, 'title');
    return title || 'Question-by-Question Breakdown';
  };

  const challenges = challengeData?.[value?.session_id];

  const studentList = studentsOnly ? getStudentList(summaryData) : [];
  const totalStudents = studentList.length;
  const currentStudent = studentList[currentStudentIndex];
  const currentStudentName = currentStudent ? getProp(currentStudent, ['name', 'student']) || `Student ${currentStudentIndex + 1}` : '';

  return (
    <SummaryWrapper>
      {questionsOnly && (
        <>
          <BreakdownHeading>{getBreakdownTitle()}</BreakdownHeading>
          <SummaryDeprecated
            value={value}
            index={index}
            challengeDataErrors={challengeDataErrors}
            setChallengeDataErrors={setChallengeDataErrors}
            challengeData={challengeData}
            isChallengeDataLoading={isChallengeDataLoading}
            fetchChallengeData={fetchChallengeData}
            currentQuestionIndex={currentQuestionIndex}
            setCurrentQuestionIndex={setCurrentQuestionIndex}
            showQuestionCard={showQuestionCard}
            reportLocation={reportLocation}
          />
        </>
      )}
      {/* Accessible carousel: a labelled group holding the current student's
          name (heading), position (status/live region), and the nav arrows. */}
      {studentsOnly && totalStudents > 0 ? (
        <StudentLayout role="group" aria-label="Student details">
          <StudentContent>
            <StudentLabel>
              <StudentName>{currentStudentName}</StudentName>
              <StudentPosition role="status">({currentStudentIndex + 1} of {totalStudents})</StudentPosition>
            </StudentLabel>
            <ParsedSummary
              summary={summaryData}
              deterministic={deterministicData}
              questionsOnly={questionsOnly}
              studentsOnly={studentsOnly}
              isShortSession={isShortSession}
              currentStudentIndex={currentStudentIndex}
              skipTitle={questionsOnly}
            />
          </StudentContent>
          <ArrowColumn>
            <NavArrow
              aria-label="Previous Student"
              onClick={() => setCurrentStudentIndex(i => i - 1)}
              disabled={currentStudentIndex === 0}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </NavArrow>
            <NavArrow
              aria-label="Next Student"
              onClick={() => setCurrentStudentIndex(i => i + 1)}
              disabled={currentStudentIndex === totalStudents - 1}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </NavArrow>
          </ArrowColumn>
        </StudentLayout>
      ) : (
        <>
          {!questionsOnly && !studentsOnly && (
            <QuestionSummary
              challenges={selectQuestionItems({
                deterministic,
                challengeData: challenges,
                summaryData,
              })}
              onSelectQuestion={onSelectQuestion}
            />
          )}
          <ParsedSummary
            summary={summaryData}
            deterministic={deterministicData}
            questionsOnly={questionsOnly}
            studentsOnly={studentsOnly}
            isShortSession={isShortSession}
            currentQuestionIndex={questionsOnly ? currentQuestionIndex : undefined}
            currentStudentIndex={studentsOnly ? currentStudentIndex : undefined}
            skipTitle={questionsOnly}
            onViewIncidents={onViewIncidents}
          />
        </>
      )}
    </SummaryWrapper>
  )
}

const SummaryWrapper = styled.div`
  margin: 1.5rem 0;
`

const BreakdownHeading = styled.h2`
  font-size: 1.75rem;
  font-weight: bold;
  margin-bottom: .75rem;
  color: #605dec;
  margin-top: 2.5rem;
`

const StudentLayout = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 1.5rem;
`

const StudentContent = styled.div`
  flex: 1;
  min-width: 0;
`

const ArrowColumn = styled.div`
  width: 116px;
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 20px;
`

const NavArrow = styled.button`
  display: flex;
  width: 48px;
  height: 48px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 100px;
  border: 1px solid var(--OKO-Green-500, #002E19);
  background: #FFF;
  cursor: pointer;
  color: #000;
  font-size: 1.25rem;
  opacity: ${({ disabled }) => (disabled ? 0.25 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  transition: opacity 0.2s ease;
`

const StudentLabel = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.25rem;
  padding: 1.5rem 0;
`

const StudentName = styled.h2`
  color: #000;
  font-family: "ABC Marfa", sans-serif;
  font-size: var(--size-724, 24px);
  font-style: normal;
  font-weight: 600;
  line-height: var(--line-height-932, 32px);
  letter-spacing: var(--Letter-Spacing-None, 0);
  margin: 0;
`

const StudentPosition = styled.span`
  color: #000;
  font-family: "ABC Marfa", sans-serif;
  font-size: var(--size-724, 24px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--line-height-932, 32px);
  letter-spacing: var(--Letter-Spacing-None, 0);
`
